import { makeAutoObservable } from 'mobx'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import { type IMainDropdownContentItem } from 'widgets/SearchDropdown/model/types'
import { mainDropdownContentItems } from 'widgets/SearchDropdown/constants/mainDropdownContentitems'
import { SearchDropdownTagsStore } from 'widgets/SearchDropdown/store/searchDropdownTagsStore'
import { SearchDropdownUsersStore } from 'widgets/SearchDropdown/store/searchDropdownUsersStore'
import { SearchDropdownInboxesStore } from 'widgets/SearchDropdown/store/searchDropdownInboxesStore'
import { SearchDropdownNumbersStore } from 'widgets/SearchDropdown/store/searchDropdownNumbersStore'
import { SearchDropdownOutboundSourceStore } from 'widgets/SearchDropdown/store/searchDropdownOutboundSourceStore'
import { SearchDropdownTimeRangeStore } from 'widgets/SearchDropdown/store/searchDropdownTimeRangeStore'
import { type ISearchDropdownStoreOptions } from 'widgets/SearchDropdown/store/types'

export interface IBreakdownFilterStoreConfig {
  additionalFilterTypes: SearchTypesEnum[]
  breakdownFilterTypes: SearchTypesEnum[]
}

export class BreakdownFilterStore {
  private _isOpen = false

  private _additionalFilterTypes: SearchTypesEnum[] = []
  private _breakdownFilterTypes: SearchTypesEnum[] = []

  private _filterTypes: SearchTypesEnum[] = []
  private _breakdownFilterType: SearchTypesEnum | null = null
  private _activeFilterType: SearchTypesEnum | null = null

  private _appliedFilterTypes: SearchTypesEnum[] = []
  private _appliedBreakdownFilterType: SearchTypesEnum | null = null
  private _appliedParams: {
    tags: number[]
    teams: number[]
    members: number[]
    numbers: number[]
    outboundSources: string[]
    timeRange: { fromTime: Date | null; toTime: Date | null } | null
  } = { tags: [], teams: [], members: [], numbers: [], outboundSources: [], timeRange: null }

  private _tagsStore: SearchDropdownTagsStore
  private _membersStore: SearchDropdownUsersStore
  private _inboxesStore: SearchDropdownInboxesStore
  private _numbersStore: SearchDropdownNumbersStore
  private _outboundSourceStore: SearchDropdownOutboundSourceStore
  private _timeRangeStore: SearchDropdownTimeRangeStore

  constructor(config: IBreakdownFilterStoreConfig, private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._additionalFilterTypes = config.additionalFilterTypes
    this._breakdownFilterTypes = config.breakdownFilterTypes

    this._tagsStore = new SearchDropdownTagsStore(this._entities)
    this._membersStore = new SearchDropdownUsersStore(this._entities, 'members')
    this._inboxesStore = new SearchDropdownInboxesStore(this._entities)
    this._numbersStore = new SearchDropdownNumbersStore(this._entities)
    this._outboundSourceStore = new SearchDropdownOutboundSourceStore()
    this._timeRangeStore = new SearchDropdownTimeRangeStore(this._entities)

    this._initFiltersOptions({ selectedMode: true, allInboxesMode: true })
  }

  private _initFiltersOptions = (options: ISearchDropdownStoreOptions) => {
    this._additionalFilterTypes.forEach((type) => {
      this._getStoreByType(type)?.initOptions(options)
    })
  }

  onBreakdownToggle = (open: boolean) => {
    this._isOpen = open
    if (!open && !this.hasAppliedFilters) {
      this.clearFilters()
    }
    this._activeFilterType = null
  }

  addFilterType = (type: SearchTypesEnum) => {
    if (this._filterTypes.includes(type)) return

    this._filterTypes = [...this._filterTypes, type]
    this._activeFilterType = type
    if (!this._breakdownFilterType) {
      this._breakdownFilterType = type
      this._applyBreakdownSettings(type)
    }
  }

  removeFilterType = (filterType: SearchTypesEnum) => {
    if (this._breakdownFilterType === filterType) return

    this._resetFilterType(filterType)
    this._filterTypes = this._filterTypes.filter((filter) => filter !== filterType)
    if (this._activeFilterType === filterType) {
      this._activeFilterType = null
    }
  }

  private _getStoreByType = (type: SearchTypesEnum) => {
    switch (type) {
      case SearchTypesEnum.tags:
        return this._tagsStore
      case SearchTypesEnum.members:
        return this._membersStore
      case SearchTypesEnum.teams:
        return this._inboxesStore
      case SearchTypesEnum.numbers:
        return this._numbersStore
      case SearchTypesEnum.outbound_sources:
        return this._outboundSourceStore
      case SearchTypesEnum.time_range:
        return this._timeRangeStore
      default:
        return null
    }
  }

  private _resetFilterType = (type: SearchTypesEnum) => {
    this._getStoreByType(type)?.resetType()
  }

  private _resetBreakdownSettings = (type: SearchTypesEnum) => {
    const store = this._getStoreByType(type)
    if (store) {
      if ('setMaxLimit' in store) {
        store.setMaxLimit(null)
      }
      store.setDisableClosing(false)
    }
  }

  private _applyBreakdownSettings = (type: SearchTypesEnum) => {
    const store = this._getStoreByType(type)
    if (store) {
      if ('setMaxLimit' in store) {
        store.setMaxLimit(25)
      }
      store.setDisableClosing(true)
    }
  }

  clearFilters = () => {
    this._additionalFilterTypes.forEach((type) => {
      this._getStoreByType(type)?.resetType()
    })

    if (this._breakdownFilterType) {
      this._resetBreakdownSettings(this._breakdownFilterType)
    }

    this._breakdownFilterType = null
    this._filterTypes = []
    this._activeFilterType = null
  }

  private _clearAppliedFilters = () => {
    this._appliedFilterTypes = []
    this._appliedBreakdownFilterType = null
    this._appliedParams = {
      tags: [],
      teams: [],
      members: [],
      numbers: [],
      outboundSources: [],
      timeRange: null,
    }
  }

  clearAllFilters = () => {
    this.clearFilters()
    this._clearAppliedFilters()
  }

  onApply = () => {
    if (!this._breakdownFilterType || !this.hasSelectedBreakdownItems) return

    this._appliedFilterTypes = [...this._filterTypes]
    this._appliedBreakdownFilterType = this._breakdownFilterType
    this._appliedParams = this.params

    this._isOpen = false
  }

  get params() {
    const tags = Array.from(this._tagsStore.selectedItemsMapIds).map((item) => item[1])
    const teams = Array.from(this._inboxesStore.selectedItemsMapIds).map((item) => item[1])
    const members = Array.from(this._membersStore.selectedItemsMapIds).map((item) => item[1])
    const numbers = Array.from(this._numbersStore.selectedItemsMapIds).map((item) => item[1])
    const outboundSources = Array.from(this._outboundSourceStore.selectedItemsMapKeys).map(
      (item) => item[1]
    )
    const timeRange = this._timeRangeStore.searchParams

    return { tags, teams, members, numbers, outboundSources, timeRange }
  }

  get filterTypes() {
    return this._filterTypes
  }

  get hasAppliedFilters() {
    return this._appliedBreakdownFilterType !== null && this._appliedFilterTypes.length > 0
  }

  get filtersCount() {
    return this._filterTypes.length
  }

  get hasFilters() {
    return this.filtersCount > 0
  }

  get hasAllAdditionalFiltersSelected() {
    return (
      this._additionalFilterTypes.length > 0 &&
      this.filtersCount >= this._additionalFilterTypes.length
    )
  }

  get breakdownFilterTypes() {
    return this._breakdownFilterTypes.reduce<IMainDropdownContentItem[]>((state, type) => {
      const item = mainDropdownContentItems.find((i) => i.type === type)
      if (item) state.push(item)
      return state
    }, [])
  }

  get additionalFilterTypesItems() {
    return this._additionalFilterTypes.reduce<IMainDropdownContentItem[]>((state, type) => {
      const item = mainDropdownContentItems.find((i) => i.type === type)
      if (item) state.push(item)
      return state
    }, [])
  }

  get activeFilterType() {
    return this._activeFilterType
  }

  get breakdownFilterType() {
    return this._breakdownFilterType
  }

  get additionalFilterTypes() {
    return this._filterTypes.filter((t) => t !== this._breakdownFilterType)
  }

  get appliedFiltersCount() {
    return this._appliedFilterTypes.length
  }

  get hasSelectedBreakdownItems() {
    if (!this._breakdownFilterType) return false
    const store = this._getStoreByType(this._breakdownFilterType)
    return store?.hasSelectedItems ?? false
  }

  get isOpen() {
    return this._isOpen
  }

  get tagsStore() {
    return this._tagsStore
  }
  get membersStore() {
    return this._membersStore
  }
  get inboxesStore() {
    return this._inboxesStore
  }
  get numbersStore() {
    return this._numbersStore
  }
  get outboundSourceStore() {
    return this._outboundSourceStore
  }
  get timeRangeStore() {
    return this._timeRangeStore
  }

  get appliedParams() {
    return this._appliedParams
  }

  get appliedBreakdownFilterType() {
    return this._appliedBreakdownFilterType
  }

  dispose = () => {
    this.clearAllFilters()

    this._isOpen = false
  }
}
